import { createContext } from 'react';

// Stores
import type { GetStoresUseCase } from '@/modules/stores/services/get-stores.usecase';
import type { SearchMerchantsUseCase } from '@/modules/stores/services/search-merchants.usecase';
import type { GetMerchantDetailsUseCase } from '@/modules/stores/services/get-merchant-details.usecase';
import type { GetStoreReviewsUseCase } from '@/modules/stores/services/get-store-reviews.usecase';
import type { GetPlaceUseCase } from '@/modules/location/services/use-cases/get-place.usecase';
import type { GetStoreItemsUseCase } from '@/modules/stores/services/get-store-items.usecase';

// Location
import type { AddUserAddressUseCase } from '@/modules/location/services/use-cases/add-user-address.usecase';
import type { EditUserAddressUseCase } from '@/modules/location/services/use-cases/edit-user-address.usecase';
import type { DeleteUserAddressUseCase } from '@/modules/location/services/use-cases/delete-user-address.usecase';
import type { ListUserAddressesUseCase } from '@/modules/location/services/use-cases/list-user-addresses.usecase';
import type { GetSelectedAddressUseCase } from '@/modules/location/services/use-cases/get-selected-address.usecase';
import type { SetSelectedAddressUseCase } from '@/modules/location/services/use-cases/set-selected-address.usecase';
import type { ConvertCoordinatesToAddressUseCase } from '@/modules/location/services/convert-coordinates-to-address.usecase';
import type { LookupCepUseCase } from '@/modules/location/services/use-cases/lookup-cep.usecase';

// Cart
import type { AddItemUseCase } from '@/modules/cart/services/add-item.usecase';
import type { RemoveItemUseCase } from '@/modules/cart/services/remove-item.usecase';
import type { IncrementQuantityUseCase } from '@/modules/cart/services/increment-quantity.usecase';
import type { DecrementQuantityUseCase } from '@/modules/cart/services/decrement-quantity.usecase';
import type { ListItemsUseCase } from '@/modules/cart/services/list-items.usecase';
import type { ClearCartUseCase } from '@/modules/cart/services/clear-cart.usecase';
import type { GetItemUseCase } from '@/modules/cart/services/get-item.usecase';
import type { EditItemUseCase } from '@/modules/cart/services/edit-item.usecase';
import type { CheckCartAvailabilityUseCase } from '@/modules/cart/services/check-cart-availability.usecase';

// Orders
import type { GetDeliveryTaxUseCase } from '@/modules/orders/services/get-delivery-tax.usecase';
import type { MakeOrderUsecase } from '@/modules/orders/services/make-order.usecase';
import type { ListOrdersUseCase } from '@/modules/orders/services/list-orders.usecase';
import type { GetPaymentWaysUseCase } from '@/modules/orders/services/get-payment-ways.usecase';
import type { GetOrderUseCase } from '@/modules/orders/services/get-order.usecase';
import type { ChangePaymentMethodUseCase } from '@/modules/orders/services/change-payment-method.usecase';
import type { RedeemCupomUseCase } from '@/modules/orders/services/redeem-cupom.usecase';
import type { ListAvailableCouponsUseCase } from '@/modules/orders/services/list-available-coupons.usecase';
import type { ReorderUseCase } from '@/modules/orders/services/reorder.usecase';
import type { CheckPendingOrdersService } from '@/modules/orders/services/check-pending-orders.service';
import type { GetAvailableScheduleUseCase } from '@/modules/orders/services/get-avaible-schedule.usecase';
import type { GetOrderStatusHistoryUseCase } from '@/modules/orders/services/get-order-status-history.usecase';
import type { RateOrderUseCase } from '@/modules/orders/services/rate-order.usecase';
import type { GetOrderChatTokenUseCase } from '@/modules/orders/services/get-order-chat-token.usecase';
import type { VerifyPaymentUseCase } from '@/modules/orders/services/verify-payment.usecase';

// Account
import type { GetProfileUseCase } from '@/modules/account/services/get-profile.usecase';
import type { UpdateProfileUseCase } from '@/modules/account/services/update-profile.usecase';
import type {
  GetProfileCompletionUseCase,
  RequestEmailConfirmationUseCase,
} from '@/modules/profile-completion/services/profile-completion.usecase';

// Clube pratiGO (PRAT-27)
import type { GetClubSummaryUseCase } from '@/modules/club/services/get-club-summary.usecase';
import type { GetClubCatalogUseCase } from '@/modules/club/services/get-club-catalog.usecase';
import type { GetClubStatementUseCase } from '@/modules/club/services/get-club-statement.usecase';

// Atendimento (PRAT-64)
import type { TrocarCodigoOperadorUseCase } from '@/modules/atendimento/services/trocar-codigo-operador.usecase';
import type { GetAtendimentoConfigUseCase } from '@/modules/atendimento/services/get-atendimento-config.usecase';
import type { ListAtendimentoClientesUseCase } from '@/modules/atendimento/services/list-atendimento-clientes.usecase';
import type { BuscarClienteTelefoneUseCase } from '@/modules/atendimento/services/buscar-cliente-telefone.usecase';
import type { SalvarAtendimentoClienteUseCase } from '@/modules/atendimento/services/salvar-atendimento-cliente.usecase';
import type { CriarPedidoAtendimentoUseCase } from '@/modules/atendimento/services/criar-pedido-atendimento.usecase';
import type { FinalizarPedidoAtendimentoUseCase } from '@/modules/atendimento/services/finalizar-pedido-atendimento.usecase';
import type { ListPedidosAtendimentoUseCase } from '@/modules/atendimento/services/list-pedidos-atendimento.usecase';
import type { GetPedidoAtendimentoUseCase } from '@/modules/atendimento/services/get-pedido-atendimento.usecase';

export interface ServiceContextProps {
  // Stores
  getStores: GetStoresUseCase;
  searchMerchants: SearchMerchantsUseCase;
  getStoreItems: GetStoreItemsUseCase;
  getMerchantDetails: GetMerchantDetailsUseCase;
  getStoreReviews: GetStoreReviewsUseCase;

  // Location
  getPlace: GetPlaceUseCase;
  addUserAddress: AddUserAddressUseCase;
  editUserAddress: EditUserAddressUseCase;
  deleteUserAddress: DeleteUserAddressUseCase;
  listUserAddresses: ListUserAddressesUseCase;
  getSelectedAddress: GetSelectedAddressUseCase;
  setSelectedAddress: SetSelectedAddressUseCase;
  convertCoordinatesToAddress: ConvertCoordinatesToAddressUseCase;
  lookupCep: LookupCepUseCase;

  // Cart
  listItems: ListItemsUseCase;
  getItem: GetItemUseCase;
  editItem: EditItemUseCase;
  addItem: AddItemUseCase;
  removeItem: RemoveItemUseCase;
  incrementQuantity: IncrementQuantityUseCase;
  decrementQuantity: DecrementQuantityUseCase;
  clearCart: ClearCartUseCase;
  checkCartAvailability: CheckCartAvailabilityUseCase;

  // Orders
  getDeliveryTax: GetDeliveryTaxUseCase;
  makeOrder: MakeOrderUsecase;
  listOrders: ListOrdersUseCase;
  getOrder: GetOrderUseCase;
  changePaymentMethod: ChangePaymentMethodUseCase;
  redeemCupom: RedeemCupomUseCase;
  listAvailableCoupons: ListAvailableCouponsUseCase;
  reorder: ReorderUseCase;
  checkPendingOrders: CheckPendingOrdersService;
  getAvailableSchedule: GetAvailableScheduleUseCase;
  getOrderStatusHistory: GetOrderStatusHistoryUseCase;
  rateOrder: RateOrderUseCase;
  getOrderChatToken: GetOrderChatTokenUseCase;
  verifyPayment: VerifyPaymentUseCase;

  // Payment
  getPaymentWays: GetPaymentWaysUseCase;

  // Account
  getProfile: GetProfileUseCase;
  updateProfile: UpdateProfileUseCase;
  getProfileCompletion: GetProfileCompletionUseCase;
  requestEmailConfirmation: RequestEmailConfirmationUseCase;

  // Clube pratiGO (PRAT-27)
  getClubSummary: GetClubSummaryUseCase;
  getClubCatalog: GetClubCatalogUseCase;
  getClubStatement: GetClubStatementUseCase;

  // Atendimento (PRAT-64)
  trocarCodigoOperador: TrocarCodigoOperadorUseCase;
  getAtendimentoConfig: GetAtendimentoConfigUseCase;
  listAtendimentoClientes: ListAtendimentoClientesUseCase;
  buscarClienteTelefone: BuscarClienteTelefoneUseCase;
  salvarAtendimentoCliente: SalvarAtendimentoClienteUseCase;
  criarPedidoAtendimento: CriarPedidoAtendimentoUseCase;
  finalizarPedidoAtendimento: FinalizarPedidoAtendimentoUseCase;
  listPedidosAtendimento: ListPedidosAtendimentoUseCase;
  getPedidoAtendimento: GetPedidoAtendimentoUseCase;
}

export const ServiceContext = createContext<ServiceContextProps>({} as ServiceContextProps);
